import React, { useState } from 'react';
import { ethers } from 'ethers';
import Card from '../common/Card';
import MiniLoader from '../common/MiniLoader';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/errors';
import { TOTAL_PACKAGES, PACKAGE_PRICES, DEFAULT_REFERRER_ID } from '../../config/contracts';

// Admin can register users, activate packages, and retopup packages without paying USDT (virtual).
const AdminUserOps = () => {
  const { logic } = useContracts();
  const notify = useNotify();

  // Register form
  const [regWallet, setRegWallet] = useState('');
  const [regRef, setRegRef] = useState(String(DEFAULT_REFERRER_ID));
  const [regBusy, setRegBusy] = useState(false);

  // Activate form
  const [actUserId, setActUserId] = useState('');
  const [actPkg, setActPkg] = useState(1);
  const [actBusy, setActBusy] = useState(false);

  // Retopup form
  const [retopUserId, setRetopUserId] = useState('');
  const [retopPkg, setRetopPkg] = useState(1);
  const [retopBusy, setRetopBusy] = useState(false);

  const doRegister = async () => {
    if (!ethers.utils.isAddress(regWallet)) { notify.error('Invalid wallet'); return; }
    const ref = parseInt(regRef, 10);
    if (!ref) { notify.error('Invalid referrer'); return; }
    setRegBusy(true);
    const l = notify.loading('Admin registering user...');
    try {
      const tx = await logic.adminRegister(regWallet, ref);
      notify.dismiss(l);
      const m = notify.loading('Confirming...');
      await tx.wait();
      notify.dismiss(m);
      notify.success('User registered');
      setRegWallet('');
    } catch (err) {
      notify.dismiss(l);
      notify.error(parseError(err));
    } finally { setRegBusy(false); }
  };

  const doActivate = async () => {
    const uid = parseInt(actUserId, 10);
    if (!uid) { notify.error('Invalid user ID'); return; }
    setActBusy(true);
    const l = notify.loading(`Admin-activating Package ${actPkg}...`);
    try {
      const tx = await logic.adminActivatePackage(uid, actPkg);
      notify.dismiss(l);
      const m = notify.loading('Confirming...');
      await tx.wait();
      notify.dismiss(m);
      notify.success(`Package ${actPkg} activated (virtual)`);
      setActUserId('');
    } catch (err) {
      notify.dismiss(l);
      notify.error(parseError(err));
    } finally { setActBusy(false); }
  };

  const doRetopup = async () => {
    const uid = parseInt(retopUserId, 10);
    if (!uid) { notify.error('Invalid user ID'); return; }
    setRetopBusy(true);
    const l = notify.loading(`Admin-retopping Package ${retopPkg}...`);
    try {
      const tx = await logic.adminRetopupPackage(uid, retopPkg);
      notify.dismiss(l);
      const m = notify.loading('Confirming...');
      await tx.wait();
      notify.dismiss(m);
      notify.success(`Package ${retopPkg} retopped (virtual) — ceiling +$${PACKAGE_PRICES[retopPkg - 1] * 5}`);
      setRetopUserId('');
    } catch (err) {
      notify.dismiss(l);
      notify.error(parseError(err));
    } finally { setRetopBusy(false); }
  };

  return (
    <div className="grid grid-3">
      <Card title="Admin Register" subtitle="Register without payment (virtual)">
        <div className="input-group">
          <label className="input-label">Wallet Address</label>
          <input className="input font-mono" placeholder="0x..." value={regWallet} onChange={(e) => setRegWallet(e.target.value)} />
        </div>
        <div className="input-group">
          <label className="input-label">Referrer ID</label>
          <input className="input" type="number" value={regRef} onChange={(e) => setRegRef(e.target.value)} />
        </div>
        <button className="btn btn-primary btn-full" onClick={doRegister} disabled={regBusy}>
          {regBusy ? <><MiniLoader /> Registering</> : 'Register User'}
        </button>
      </Card>

      <Card title="Admin Activate" subtitle="Activate package without payment">
        <div className="input-group">
          <label className="input-label">User ID</label>
          <input className="input" type="number" placeholder="1234" value={actUserId} onChange={(e) => setActUserId(e.target.value)} />
        </div>
        <div className="input-group">
          <label className="input-label">Package</label>
          <select className="input" value={actPkg} onChange={(e) => setActPkg(parseInt(e.target.value, 10))}>
            {Array.from({ length: TOTAL_PACKAGES }, (_, i) => i + 1).map((p) => (
              <option key={p} value={p}>Package {p} — ${PACKAGE_PRICES[p - 1]}</option>
            ))}
          </select>
        </div>
        <div className="mt-md text-muted" style={{ fontSize: 12, minHeight: 44 }}>
          Note: Activation records package as active but does not pay income to uplines.
        </div>
        <button className="btn btn-primary btn-full" onClick={doActivate} disabled={actBusy}>
          {actBusy ? <><MiniLoader /> Activating</> : 'Activate Package'}
        </button>
      </Card>

      <Card title="Admin Retopup" subtitle="Retopup active package without payment">
        <div className="input-group">
          <label className="input-label">User ID</label>
          <input className="input" type="number" placeholder="1234" value={retopUserId} onChange={(e) => setRetopUserId(e.target.value)} />
        </div>
        <div className="input-group">
          <label className="input-label">Package</label>
          <select className="input" value={retopPkg} onChange={(e) => setRetopPkg(parseInt(e.target.value, 10))}>
            {Array.from({ length: TOTAL_PACKAGES }, (_, i) => i + 1).map((p) => (
              <option key={p} value={p}>Package {p} — ${PACKAGE_PRICES[p - 1]} (+${PACKAGE_PRICES[p - 1] * 5} ceiling)</option>
            ))}
          </select>
        </div>
        <div className="mt-md text-muted" style={{ fontSize: 12, minHeight: 44 }}>
          Note: Package must already be active. Extends ceiling by 5x price, preserves earned amount, no income distribution.
        </div>
        <button className="btn btn-primary btn-full" onClick={doRetopup} disabled={retopBusy}>
          {retopBusy ? <><MiniLoader /> Retopping</> : '🔁 Retopup Package'}
        </button>
      </Card>
    </div>
  );
};

export default AdminUserOps;
